// components/organisms/Sidebar/Sidebar.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { BsLayoutSidebar } from 'react-icons/bs';
import {
    FaChevronLeft,
    FaChevronUp,
    FaChevronDown,
} from 'react-icons/fa';

const getChildren = (item) => {
    if (!item.children || !Array.isArray(item.children)) return [];
    return item.children;
};

const matchesRoute = (url, asPath) => {
    if (!url) return false;
    return asPath === url || asPath.startsWith(url + '/');
};

const findActiveParent = (items, asPath) => {
    for (const item of items) {
        if (asPath === item.url) return null;
        if (item.url && asPath.startsWith(item.url + '/')) return item;
    }
    return null;
};

const SidebarItemLink = ({ item, isActive, isCurrent, isCollapsed }) =>
    item.isNavGroup
        ? (
            <span
                className="sidebar-item sidebar-item--group-label"
                title={isCollapsed ? item.title : undefined}
            >
                {item.icon && <span className="sidebar-icon" aria-hidden="true">{item.icon}</span>}
                {!isCollapsed && <span className="sidebar-item-title">{item.title}</span>}
            </span>
        ) : (
            <Link
                href={item.url || '#'}
                className={['sidebar-item', isActive ? 'active' : '', isCurrent ? 'current' : ''].filter(Boolean).join(' ')}
                title={isCollapsed ? item.title : undefined}
            >
                {item.icon && <span className="sidebar-icon" aria-hidden="true">{item.icon}</span>}
                {!isCollapsed && <span className="sidebar-item-title">{item.title}</span>}
            </Link>
        );

const ScrollFadeList = ({ children }) => {
    const scrollRef = useRef(null);
    const [canUp,   setCanUp]   = useState(false);
    const [canDown, setCanDown] = useState(false);

    const check = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanUp(el.scrollTop > 4);
        setCanDown(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        check();
        el.addEventListener('scroll', check, { passive: true });
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => { el.removeEventListener('scroll', check); ro.disconnect(); };
    }, [check, children]);

    const scrollByDir = (dir) =>
        scrollRef.current?.scrollBy({ top: dir * 120, behavior: 'smooth' });

    return (
        <div className="sidebar-scroll-wrap">
            {canUp && (
                <div className="sidebar-scroll-fade sidebar-scroll-fade--top">
                    <button
                        className="sidebar-scroll-btn"
                        onClick={() => scrollByDir(-1)}
                        aria-label="Scroll up"
                    >
                        <FaChevronUp className="icon-xs" />
                    </button>
                </div>
            )}
            <div ref={scrollRef} className="sidebar-scroll-inner">
                {children}
            </div>
            {canDown && (
                <div className="sidebar-scroll-fade sidebar-scroll-fade--bottom">
                    <button
                        className="sidebar-scroll-btn"
                        onClick={() => scrollByDir(1)}
                        aria-label="Scroll down"
                    >
                        <FaChevronDown className="icon-xs" />
                    </button>
                </div>
            )}
        </div>
    );
};

const Sidebar = ({
    title     = '',
    rootUrl   = null,
    brand     = null,
    items     = [],
    footer    = null,
    className = '',
    isCollapsed: isCollapsedProp,
    onToggleCollapse: onToggleCollapseProp,
}) => {
    const router = useRouter();

    const isControlled = isCollapsedProp !== undefined;
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const isCollapsed  = isControlled ? isCollapsedProp : internalCollapsed;
    const handleToggle = onToggleCollapseProp ?? (() => setInternalCollapsed(v => !v));

    const [openItems, setOpenItems] = useState(new Set());

    const toggleOpen = (url) => {
        setOpenItems(prev => {
            const next = new Set(prev);
            if (next.has(url)) {
                next.delete(url);
            } else {
                next.add(url);
            }
            return next;
        });
    };

    useEffect(() => {
        const autoOpen = new Set();
        const walk = (list) => {
            list.forEach(item => {
                if (!item.url) return;
                const strippedUrl = item.url.replace(/^\/[a-z]{2}\//, '/');
                if (router.asPath.startsWith(strippedUrl)) {
                    autoOpen.add(item.url);
                }
                if (item.children) walk(item.children);
            });
        };
        walk(items);
        if (autoOpen.size > 0) {
            setOpenItems(prev => new Set([...prev, ...autoOpen]));
        }
    }, [router.asPath, items]);

    const activeParent = findActiveParent(items, router.asPath);
    const isInsideChildSection = Boolean(activeParent);

    const sidebarClasses = [
        'sidebar',
        isCollapsed && 'sidebar-collapsed',
        className,
    ].filter(Boolean).join(' ');

    return (
        <aside className={sidebarClasses}>

            <div className="sidebar-header">
                {!isCollapsed && (
                    <div className="sidebar-brand">
                        {brand?.logo && (
                            <img
                                src={brand.logo}
                                alt={brand.name || title}
                                className="sidebar-brand-logo"
                            />
                        )}
                        <span className="sidebar-header-title">
                            {brand?.name || title}
                        </span>
                    </div>
                )}
                <button
                    className="sidebar-collapse-btn"
                    onClick={handleToggle}
                    aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <BsLayoutSidebar className="icon-sm" />
                </button>
            </div>

            {isInsideChildSection && !isCollapsed && (
                <Link href={activeParent.url} className="sidebar-back">
                    <FaChevronLeft className="icon-xs" />
                    <span className="sidebar-back-label">{activeParent.title}</span>
                </Link>
            )}

            <ScrollFadeList>
                <nav className="sidebar-nav">
                    {isInsideChildSection
                        ? renderChildren(
                            getChildren(activeParent),
                            router.asPath,
                            openItems,
                            toggleOpen,
                            isCollapsed
                        )
                        : items.map((item, index) =>
                            renderItem(
                                item,
                                index,
                                router.asPath,
                                openItems,
                                toggleOpen,
                                isCollapsed
                            )
                        )
                    }
                </nav>
            </ScrollFadeList>

            {footer && !isCollapsed && (
                <div className="sidebar-footer">{footer}</div>
            )}
        </aside>
    );
};

function renderItem(item, index, asPath, openItems, toggleOpen, isCollapsed) {
    if (item.type === 'divider') {
        return <div key={`divider-${index}`} className="sidebar-divider" />;
    }

    const kids      = getChildren(item);
    const hasKids   = kids.length > 0;
    const isActive  = matchesRoute(item.url, asPath);
    const isCurrent = item.url.replace(/^\/[a-z]{2}\//, '/') === asPath;
    const isOpen    = openItems.has(item.url);
    const itemKey   = item.url || `item-${index}`;

    return (
        <div
            key={itemKey}
            className={`sidebar-accordion-item${isOpen ? ' is-open' : ''}`}
        >
            <div className={`sidebar-item-row${isActive ? ' active' : ''}`}>
                <SidebarItemLink
                    item={item}
                    isActive={isActive}
                    isCurrent={isCurrent}
                    isCollapsed={isCollapsed}
                />
                {hasKids && !isCollapsed && (
                    <button
                        className="sidebar-drill-btn"
                        onClick={() => toggleOpen(item.url)}
                        aria-label={isOpen ? `Collapse ${item.title}` : `Expand ${item.title}`}
                        aria-expanded={isOpen}
                    >
                        <span className="nav-chevron" aria-hidden="true" />
                    </button>
                )}
            </div>

            {hasKids && isOpen && !isCollapsed && (
                <div className="sidebar-accordion-children">
                    {renderChildren(kids, asPath, openItems, toggleOpen, isCollapsed)}
                </div>
            )}
        </div>
    );
}

function renderChildren(kids, asPath, openItems, toggleOpen, isCollapsed) {
    return kids.map((child, index) =>
        renderItem(child, index, asPath, openItems, toggleOpen, isCollapsed)
    );
}

Sidebar.propTypes = {
    title:            PropTypes.string,
    rootUrl:          PropTypes.string,
    brand:            PropTypes.shape({
        name: PropTypes.string,
        logo: PropTypes.string,
    }),
    items:            PropTypes.array.isRequired,
    footer:           PropTypes.node,
    className:        PropTypes.string,
    isCollapsed:      PropTypes.bool,
    onToggleCollapse: PropTypes.func,
};

export default Sidebar;